import { type Completion, type CompletionContext, pickedCompletion, startCompletion } from "@codemirror/autocomplete";
import { LRLanguage } from "@codemirror/language";
import type { EditorState } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import {
  type KeywordsType,
  type SDK_Colors,
  type SDK_Fonts,
  alignOptions,
  anchorOptions,
  animateOptions,
  autospaceOptions,
  chartTypeOptions,
  colorsInBoostOrder,
  decorationOptions,
  effects,
  filterPreviews,
  getEnglishToLocaleKeywordMap,
  getEnglishToLocaleModifierMap,
  getLocale,
  getModifierCategory,
  hideOnOptions,
  keyWordsWithAutoComplete,
  keywords,
  keywordsObject,
  keywordsRegExString,
  modifierObject,
  modifierRegex,
  modifiersWithAutoComplete,
  moveByOptions,
  shadowOptions,
  strings,
  systemFonts,
  translateJadleToEnglish,
  weightOptions
} from "@quickpoint.me/shared";
import { parserWithMetaData } from "@quickpoint.me/shared/editor";
import { postInternalApplicationAction } from "../messageBus";
import {
  appVarsStateField,
  audioFilesStateField,
  backLinksStateField,
  dataFilesStateField,
  filterColorsStateField,
  filterNamesStateField,
  imageFilesStateField,
  loadedFilterFontsStateField
} from "./recoilStatesForCodeMirror";


export const ryamlLanguage = LRLanguage.define({
  parser: parserWithMetaData,
  languageData: {
    autocomplete: rYamlCompletions,
  },
});

/**
 * This file contains all the logic for the auto complete
 * Autocomplete requires a transaction watcher in CodeMirror to open the autocomplete menu
 * This will not trigger the open autocomplete menu
 * It only provides the content of the autocomplete menu
 */

/**
 * Curated common languages offered as `language:` value completions (WCAG
 * 3.1.1). Codes are BCP-47 primary subtags; the value is validated again on the
 * viewer side, so this list is a convenience, not the source of truth.
 */
const LANGUAGE_OPTIONS: { code: string; name: string }[] = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "it", name: "Italian" },
  { code: "pt", name: "Portuguese" },
  { code: "nl", name: "Dutch" },
  { code: "pl", name: "Polish" },
  { code: "ru", name: "Russian" },
  { code: "uk", name: "Ukrainian" },
  { code: "ar", name: "Arabic" },
  { code: "he", name: "Hebrew" },
  { code: "hi", name: "Hindi" },
  { code: "zh", name: "Chinese" },
  { code: "ja", name: "Japanese" },
  { code: "ko", name: "Korean" },
  { code: "tr", name: "Turkish" },
  { code: "sv", name: "Swedish" },
  { code: "da", name: "Danish" },
  { code: "nb", name: "Norwegian" },
  { code: "fi", name: "Finnish" },
  { code: "cs", name: "Czech" },
  { code: "el", name: "Greek" },
  { code: "ro", name: "Romanian" },
  { code: "hu", name: "Hungarian" },
  { code: "id", name: "Indonesian" },
  { code: "vi", name: "Vietnamese" },
  { code: "th", name: "Thai" },
  { code: "ga", name: "Irish" },
];

/** Translate a keyword/modifier label to its English form for matching */
function toEnglishLabel(label: string): string {
  const locale = getLocale();
  if (!locale || locale === "en") return label;
  return translateJadleToEnglish(label, locale);
}

export const onAutoCompleteTransaction = () => {
  return EditorView.updateListener.of((update) => {
    for (const tr of update.transactions) {
      const completion = tr.annotation(pickedCompletion);
      if (completion) {
        // Check against English keywords (translate the picked label back)
        const englishLabel = toEnglishLabel(completion.label);
        if (
          keyWordsWithAutoComplete().includes(englishLabel)
          || modifiersWithAutoComplete().includes(englishLabel)
        ) {
          startCompletion(update.view);
        }
      }
    }
  });
};

// a function that checks each previous line to see if it matches a keyword returns the match and stops
const matchPreviousKeyword = (context: CompletionContext) => {
  // start at the line before the current line
  let previousLineNumber = context.state.doc.lineAt(context.pos).number - 1;
  let result = "";

  // a line with no keyword is considered a paragraph:
  // this matches lines that can have spaces but also must have characters.
  const paragraphRegEx = /^(?=.*\S).+$/gm;

  while (result === "" && previousLineNumber > 0) {
    const text = context.state.doc.line(previousLineNumber).text;
    // Translate line to English for keyword matching (no-op for en locale)
    const englishText = translateJadleToEnglish(text);
    const match = englishText.match(keywordsRegExString(keywords));
    const paragraphMatch = text.match(paragraphRegEx);
    const modifierMatch = englishText.match(modifierRegex);
    // order matters as paragraphMatch will match any line with characters
    if (paragraphMatch && !modifierMatch) {
      result = keywordsObject["paragraph:"].keyword;
    }

    // order matters
    if (match?.[0]) {
      result = match[0];
    }

    previousLineNumber--;
  }

  return result;
};

const matchLine = (context: CompletionContext) => {
  const currentLine = context.state.doc.lineAt(context.pos);
  // Translate line to English for keyword matching (no-op for en locale)
  const englishLineText = translateJadleToEnglish(currentLine.text);
  let from = currentLine.from;
  const requiresAutoComplete = [...keyWordsWithAutoComplete(), ...modifiersWithAutoComplete()];
  let whitespace = 0;
  let result = "";

  // order matters here.. result is overwritten in the forEach loop of it finds a match
  const startOfLine = context.matchBefore(/^.*/);
  if (startOfLine) result = strings.startOfLine;

  const startOfLinePlusTab = context.matchBefore(new RegExp(/^\s{2}.*/));
  if (startOfLinePlusTab) {
    result = strings.startOfLinePlusTab;
    from = from + 2; // make codemirror ignore the spaces before the text to be auto completed when indented
  }

  // open an autocomplete window for certain words when they are typed.
  // Match against English keywords (the line was translated above)
  for (const keyword of requiresAutoComplete) {
    const regEx = new RegExp(
      `(?:\\s{2}${keyword})(?<modWhitespace>\\s?)\\s?|(?:^${keyword})(?<keyWhitespace>\\s?)\\s?`,
    );
    const match = englishLineText.match(regEx);
    if (match?.groups) {
      result = keyword;
      whitespace = match.groups.modWhitespace
        ? match.groups.modWhitespace.length
        : match.groups.keyWhitespace
          ? match.groups.keyWhitespace.length
          : 0;
    }
  }

  return { result, from, to: currentLine.to, whitespace };
};

/** Translate an English keyword to its locale equivalent for display */
function localeKeyword(englishKeyword: string): string {
  const locale = getLocale();
  if (!locale || locale === "en") return englishKeyword;
  const map = getEnglishToLocaleKeywordMap(locale);
  // Keyword includes colon (e.g. "title:"), strip it, translate, re-add
  const withoutColon = englishKeyword.replace(/:$/, "");
  const translated = map[withoutColon];
  return translated ? `${translated}:` : englishKeyword;
}

/** Translate an English modifier to its locale equivalent for display */
function localeModifier(englishModifier: string): string {
  const locale = getLocale();
  if (!locale || locale === "en") return englishModifier;
  const map = getEnglishToLocaleModifierMap(locale);
  const withoutColon = englishModifier.replace(/:$/, "");
  const translated = map[withoutColon];
  return translated ? `${translated}:` : englishModifier;
}

// only keywords
const startOfLineOptions = () => {
  const keys: Completion[] = [];
  for (const keyword of keywords) {
    // Filter out hidden keywords
    if (
      keyword !== keywordsObject["paragraph:"].keyword &&
      !keywordsObject[keyword].hide
    ) {
      const label = localeKeyword(keyword);
      switch (keyword) {
        case keywordsObject["*:"].keyword:
        case keywordsObject["**:"].keyword:
        case keywordsObject["#:"].keyword:
        case keywordsObject["##:"].keyword:
          keys.push({ label, section: "Bullets" });
          break;
        default:
          keys.push({ label });
      }
    }
  }
  return keys;
};

// only modifiers
const startOfLinePlusTabOptions = (keyword: KeywordsType) => {
  // Filter out hidden modifiers
  const modifiers = keywordsObject[keyword].modifiers
    .filter(mod => !modifierObject[mod]?.hide)
    .map(mod => {
      return {
        label: localeModifier(mod),
        section: getModifierCategory(mod),
      };
    });
  return modifiers;
};

const fontOptions = (filterFonts: SDK_Fonts) => {
  const opts = [];

  const filterFontNames = Object.values(filterFonts).reduce((fonts, font) => {
    if (font) fonts.push(font);
    return fonts;
  }, <string[]>[]);

  const allFonts = [...filterFontNames, ...systemFonts];

  for (const font of allFonts) {
    opts?.push({
      label: font,
      type: modifierObject["font:"].keyword,
    });
  }

  return opts;
};

const colorOptions = (colors: SDK_Colors) => {
  const opts: Completion[] = [];

  for (const color in colors) {
    opts?.push({
      label: color,
      type: modifierObject["color:"].keyword,
      // @ts-expect-error: cant remove __typename from codeGen types
      boost: colorsInBoostOrder.indexOf(color),
      section: "colors",
    });
  }
  return opts;
};

export const addPreviewToOptions = (
  completion: Completion,
  state: EditorState,
) => {
  if (completion.type === modifierObject["color:"].keyword) {
    return addColorToOptions(completion, state);
  }

  if (completion.type === modifierObject["border-color:"].keyword) {
    return addColorToOptions(completion, state);
  }

  if (completion.type === keywordsObject["image:"].keyword) {
    const c = completion as ImageCompletion;
    return addImageToOptions(c);
  }

  if (completion.type === keywordsObject["filter:"].keyword) {
    const c = completion as ImageCompletion;
    return addFilterPreviewToOptions(c)
  }

  return null;
};

export const addColorToOptions = (
  completion: Completion,
  state: EditorState,
) => {
  const node = new DocumentFragment();

  const colorBlock = document.createElement("div");
  const colors = state.field(filterColorsStateField);
  // if it's not a color don't render anything
  if (completion.label in colors) {
    const color = colors[completion.label as keyof typeof colors];
    if (color) colorBlock.style.backgroundColor = color;
    colorBlock.style.width = "1rem";
    colorBlock.style.height = "1rem";
    colorBlock.style.display = "inline-block";
    colorBlock.className = "color-block";
    node.append(colorBlock);
  }

  return node;
};

export const addFontToOptions = (
  completion: Completion,
) => {
  const node = new DocumentFragment();

  const fontBlock = document.createElement("div");

  fontBlock.style.width = "1rem";
  fontBlock.style.height = "1rem";
  fontBlock.style.display = "inline-block";
  fontBlock.style.fontFamily = completion.label;
  fontBlock.innerText = "aB";
  node.append(fontBlock);

  return node;
};

export interface ImageCompletion extends Completion {
  url: string;
}

export const addImageToOptions = (
  completion: ImageCompletion,
) => {
  const node = new DocumentFragment();

  const imageBlock = document.createElement("div");

  imageBlock.style.backgroundImage = `url(${completion.url})`;
  imageBlock.style.backgroundSize = "contain";
  imageBlock.style.backgroundRepeat = "no-repeat";
  imageBlock.style.width = "2rem";
  imageBlock.style.height = "2rem";
  imageBlock.style.display = "inline-block";
  imageBlock.className = "image-preview";
  node.append(imageBlock);

  return node;
};

export const addFilterPreviewToOptions = (
  completion: ImageCompletion,
) => {
  const node = new DocumentFragment();

  const imageBlock = document.createElement("div");

  imageBlock.style.backgroundImage = `url(${completion.url})`;
  imageBlock.style.backgroundSize = "contain";
  imageBlock.style.backgroundRepeat = "no-repeat";
  imageBlock.style.width = "9.15rem";
  imageBlock.style.height = "5rem";
  imageBlock.style.display = "inline-block";
  imageBlock.className = "image-preview";
  node.append(imageBlock);

  return node;
};

export function rYamlCompletions(context: CompletionContext) {
  const matchType = matchLine(context);
  if (!matchType) return null;

  const previousKeyword = matchPreviousKeyword(context);

  let from = matchType.from + matchType.result.length + matchType.whitespace;
  let to = matchType.to;

  const appVars = context.state.field(appVarsStateField);
  const colors = context.state.field(filterColorsStateField);
  const filterFonts = context.state.field(loadedFilterFontsStateField);
  const filterNames = context.state.field(filterNamesStateField);
  const images = context.state.field(imageFilesStateField);
  const dataFiles = context.state.field(dataFilesStateField);
  const audioFiles = context.state.field(audioFilesStateField)

  const backLinks = context.state.field(backLinksStateField);




  let options: {
    label: string;
    boost?: number;
    type?: string;
    detail?: string;
  }[] = [];

  if (matchType.result === strings.startOfLine) {
    from = matchType.from;
    to = matchType.to;
    options = startOfLineOptions();
  }

  if (matchType.result === strings.startOfLinePlusTab && previousKeyword) {
    from = matchType.from;
    to = matchType.to;
    options = startOfLinePlusTabOptions(previousKeyword as KeywordsType);
  }

  const uploadImageOption: Completion = {
    label: "Upload image…",
    type: "upload-image",
    apply: () => {
      postInternalApplicationAction("", strings.messages.toggleFilesPanel);
    },
    section: "images",
    boost: 2,
  };

  const imageOptions = [
    uploadImageOption,
    ...images.map(image => {
      const boost = image.stackId === appVars.stackId ? 1 : 0;
      return {
        label: image.userDefinedName ? image.userDefinedName : "error",
        url: image.url,
        type: keywordsObject["image:"].keyword,
        boost: 1 + boost,
        section: "images",
      } as ImageCompletion;
    }),
  ];


  const filterOptions = filterNames.map(filter => {
    return {
      label: filter ? filter : "error",
      //@ts-expect-error
      url: filterPreviews[filter],
      type: keywordsObject["filter:"].keyword,
      section: "filters",
    };
  });

  switch (matchType.result) {
    case modifierObject["align:"].keyword:
      options = alignOptions.map(align => {
        return {
          label: align,
        };
      });
      break;

    case modifierObject["animate:"].keyword:
      options = animateOptions.map(animate => {
        return {
          label: animate,
        };
      });
      break;

    case modifierObject["anchor:"].keyword:
      options = anchorOptions.map(anchor => {
        return {
          label: anchor,
        };
      });
      break;

    case keywordsObject["audio:"].keyword:
      options = audioFiles.map(qpFile => {
        return {
          label: qpFile.userDefinedName
        };
      });
      break;


    case keywordsObject["autospace:"].keyword:
      options = autospaceOptions.map(autospace => {
        return {
          label: autospace
        };
      });
      break;

    case keywordsObject["language:"].keyword:
      // Curated common languages so a hand-editing author picks a real BCP-47
      // code (the label is the inserted value; the name shows as detail). The
      // value is validated/canonicalised again on the viewer side.
      options = LANGUAGE_OPTIONS.map((lang) => {
        return {
          label: lang.code,
          detail: lang.name,
        };
      });
      break;

    case modifierObject["backlink:"].keyword:
      options = backLinks.map(backLink => {
        return {
          label: backLink,
        };
      });
      break;

    case keywordsObject["background:"].keyword:
    case modifierObject["background:"].keyword:
      options = colorOptions(colors).concat(imageOptions);
      for (const option of options) {
        // preserve custom types like upload-image
        if (option.type === "upload-image") continue;
        // @ts-expect-error: difficult to type
        if (colors[option.label]) {
          option.type = modifierObject["color:"].keyword;
        } else {
          option.type = keywordsObject["image:"].keyword;
        }
      }
      break;

    case keywordsObject["chart:"].keyword:
      options = dataFiles.map(qpFile => {
        return {
          label: qpFile.userDefinedName
        };
      });
      break;

    case modifierObject["chart-type:"].keyword:
      options = chartTypeOptions.map(chartType => {
        return {
          label: chartType,
        };
      });
      break;

    case modifierObject["decoration:"].keyword:
      options = decorationOptions.map(decoration => {
        return {
          label: decoration,
        };
      });
      break;

    case modifierObject["color:"].keyword:
    case modifierObject["border-color:"].keyword:
      options = colorOptions(colors);
      break;

    case modifierObject["effect:"].keyword:
      options = Array.from(effects).map(effect => {
        return {
          label: effect,
        };
      });
      break;

    case keywordsObject["filter:"].keyword:
      options = filterOptions;
      break;

    case modifierObject["font:"].keyword:
      options = fontOptions(filterFonts);
      break;

    case modifierObject["hideOn:"].keyword:
      options = hideOnOptions.map(hideOn => {
        return {
          label: hideOn
        };
      });
      break;

    case keywordsObject["image:"].keyword:
      options = imageOptions;
      break;

    case keywordsObject["moveBy:"].keyword:
      options = moveByOptions.map(moveBy => {
        return {
          label: moveBy
        };
      });
      break;


    case modifierObject["shadow:"].keyword:
      options = shadowOptions.map(shadowType => {
        return {
          label: shadowType,
        };
      });
      break;


    case modifierObject["weight:"].keyword:
      options = weightOptions.map(weight => {
        return {
          label: weight,
        };
      });
  }

  const completion = {
    from,
    to,
    options,
  };
  return completion;
}
